import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Check } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';

import { API, RAZORPAY_KEY_ID } from '@/config';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    axios.get(`${API}/plans`).then(r => setPlans(r.data)).catch(() => {});
    loadRazorpayScript();
  }, []);

  const handleSubscribe = useCallback(async (planKey) => {
    if (!user) { navigate('/signup'); return; }
    if (planKey === user.plan) return;

    // Downgrade to free
    if (planKey === 'free') {
      if (!window.confirm('Cancel your subscription and downgrade to Free?')) return;
      setProcessing(planKey);
      try {
        await axios.post(`${API}/subscriptions/cancel`);
        await refreshUser();
        toast.success('Downgraded to Free plan');
        navigate('/dashboard');
      } catch (err) {
        // If no active sub, just update plan directly
        try {
          await axios.put(`${API}/auth/plan`, { plan: 'free' });
          await refreshUser();
          toast.success('Switched to Free plan');
          navigate('/dashboard');
        } catch {
          toast.error('Failed to downgrade');
        }
      } finally { setProcessing(null); }
      return;
    }

    // Paid plan — create Razorpay subscription
    setProcessing(planKey);
    try {
      const res = await axios.post(`${API}/subscriptions/create`, { plan: planKey });
      const { subscription_id, plan_name } = res.data;

      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error('Payment system failed to load'); setProcessing(null); return; }

      const options = {
        key: RAZORPAY_KEY_ID,
        subscription_id: subscription_id,
        name: 'Whisper.AI',
        description: `${plan_name} Plan — Monthly Subscription`,
        handler: async (response) => {
          // Verify payment
          try {
            const verifyRes = await axios.post(`${API}/subscriptions/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_signature: response.razorpay_signature,
            });
            await refreshUser();
            toast.success(verifyRes.data.message || `Upgraded to ${plan_name}!`);
            navigate('/dashboard');
          } catch {
            toast.error('Payment verification failed. Contact support.');
          }
          setProcessing(null);
        },
        modal: {
          ondismiss: () => { setProcessing(null); },
        },
        prefill: {
          name: user.name || '',
          email: user.email || '',
        },
        theme: {
          color: '#10B981',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(resp.error?.description || 'Payment failed');
        setProcessing(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start payment');
      setProcessing(null);
    }
  }, [user, refreshUser, navigate]);

  const orderedPlans = ['free', 'single', 'starter', 'pro', 'business'];

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      <SiteHeader />

      <main className="max-w-5xl mx-auto px-6 py-8 sm:py-16">
        <div className="text-center mb-12">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Pricing</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-3" data-testid="pricing-title">
            Simple, transparent pricing
          </h1>
          <p className="text-base text-muted-foreground max-w-lg mx-auto">
            Start free, scale as you grow. Every plan includes our full AI chat widget with GPT-4o-mini.
          </p>
          <p className="text-sm text-muted-foreground/70 max-w-md mx-auto mt-2">
            Each chat bot is a unique AI assistant with its own name, personality, and knowledge base — embed it on any website.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="pricing-grid">
          {orderedPlans.map((key) => {
            const plan = plans[key];
            if (!plan) return null;
            const isCurrent = user?.plan === key;
            const isPopular = key === 'pro';
            const isProcessing = processing === key;

            return (
              <div
                key={key}
                className={`p-6 rounded-xl border flex flex-col ${
                  isPopular
                    ? 'border-[#10B981] bg-[#10B981]/5 relative'
                    : 'border-border bg-card'
                }`}
                data-testid={`plan-card-${key}`}
              >
                {isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#10B981] text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider">
                    Popular
                  </span>
                )}
                <h3 className="text-sm font-semibold text-foreground">{plan.name}</h3>
                <div className="mt-3 mb-4">
                  <span className="text-3xl font-bold text-foreground">${plan.price}</span>
                  {plan.price > 0 && <span className="text-sm text-muted-foreground">/mo</span>}
                </div>
                <ul className="space-y-2.5 flex-1 mb-6">
                  {plan.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-[#10B981] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSubscribe(key)}
                  disabled={isCurrent || isProcessing}
                  variant={isPopular ? 'default' : 'outline'}
                  className={`w-full ${isPopular ? 'bg-[#10B981] hover:bg-[#059669] text-white' : ''}`}
                  data-testid={`select-plan-${key}`}
                >
                  {isCurrent ? 'Current Plan' : isProcessing ? 'Processing...' : plan.price === 0 ? 'Get Started' : `$${plan.price}/mo`}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8 mb-16">
          Secure payments via Razorpay. Cancel anytime. All plans renew monthly.
        </p>

        {/* AI Intelligence & Crawl Comparison */}
        <div className="max-w-4xl mx-auto mb-16" data-testid="feature-comparison">
          <h3 className="text-lg font-semibold text-foreground text-center mb-6">AI Intelligence & Features by Plan</h3>
          <div className="rounded-xl border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Feature</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Free</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Single</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Starter</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-[#10B981] uppercase tracking-wider">Pro</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Business</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  { feature: 'Widget Tabs', values: ['Home + Chat', 'All 4 tabs', 'All 4 tabs', 'All 4 tabs', 'All 4 tabs'] },
                  { feature: 'AI Intelligence', values: ['Knowledge Base only', 'Knowledge Base + Basic AI', 'Smart AI', 'Advanced AI', 'Advanced AI'] },
                  { feature: 'Knowledge Base', values: [true, true, true, true, true] },
                  { feature: 'Website Crawl', values: [false, '5 pages', '5 pages', '20 pages', '20 pages'] },
                  { feature: 'Training Files', values: [false, '3 files', '5 files', '15 files', 'Unlimited'] },
                  { feature: 'Human Help', values: [false, '5/mo', '20/mo', 'Unlimited', 'Unlimited'] },
                  { feature: 'Chat Bots', values: ['1', '1', '3', '10', 'Unlimited'] },
                  { feature: 'Messages/mo', values: ['15 (+$1 top-up)', '500', '2,000', '10,000', '50,000'] },
                  { feature: 'Custom Theme', values: [false, true, true, true, true] },
                  { feature: 'Accent Color', values: [true, true, true, true, true] },
                  { feature: 'Hide Badge', values: [false, true, true, true, true] },
                  { feature: 'Custom Badge Text', values: [false, false, true, true, true] },
                  { feature: 'Company Logo', values: [false, false, false, true, true] },
                  { feature: 'White-label', values: [false, false, false, false, true] },
                  { feature: 'Dedicated Support', values: [false, false, false, false, true] },
                ].map((row) => (
                  <tr key={row.feature} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 text-sm font-medium text-foreground">{row.feature}</td>
                    {row.values.map((val, i) => (
                      <td key={i} className={`text-center px-3 py-2.5 text-xs ${i === 3 ? 'bg-[#10B981]/5' : ''}`}>
                        {val === true ? <Check className="h-4 w-4 text-[#10B981] mx-auto" /> :
                         val === false ? <span className="text-muted-foreground/40">—</span> :
                         <span className="text-foreground">{val}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
